import type {Session} from '../types/session'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {COPILOT_APP_SLUG} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import type {UITask} from '../types/task'
import type {HydratedAPITask} from '../types/api-task'
import {type AgentProfile, DEFAULT_PROFILE, mergeWithDefault} from './agent-profile'
import {CLI_PROFILE} from './profiles/cli-profile'
import {COPILOT_APP_PROFILE} from './profiles/copilot-app-profile'
import {CLOUD_AGENT_PROFILE} from './profiles/cloud-agent-profile'
import {CODE_REVIEW_PROFILE} from './profiles/code-review-profile'
import {VSCODE_PROFILE} from './profiles/vscode-profile'
import {JETBRAINS_PROFILE} from './profiles/jetbrains-profile'
import {ChatTrailingVisual, EnvironmentTrailingVisual, SandboxTrailingVisual} from './profiles/DefaultTrailingVisual'
import {isQuickChatTask} from '../utils/is-quick-chat-task'

/**
 * Registry of known agent profiles keyed by slug.
 * To add a new agent type, import its profile and add an entry here.
 */
const PROFILE_REGISTRY: Record<string, Partial<AgentProfile>> = {
  'copilot-developer-cli': CLI_PROFILE,
  'copilot-developer-app': COPILOT_APP_PROFILE,
  'copilot-developer': CLOUD_AGENT_PROFILE,
  'copilot-pr-reviews': CODE_REVIEW_PROFILE,
  'vscode-chat': VSCODE_PROFILE,
  'jetbrains-chat': JETBRAINS_PROFILE,
}

/**
 * Resolves an AgentProfile from a slug.
 * Looks up the slug in the profile registry and returns a merged profile,
 * or DEFAULT_PROFILE if the slug is unknown or absent.
 */
export function resolveAgentProfile(slug?: string): AgentProfile {
  if (slug === COPILOT_APP_SLUG && !copilotFeatureFlags.appRemoteSessions) {
    // Rollout must not turn private sessions into copyable public links.
    return mergeWithDefault({privateSessions: true})
  }
  if (slug && PROFILE_REGISTRY[slug]) {
    return mergeWithDefault(PROFILE_REGISTRY[slug])
  }
  return DEFAULT_PROFILE
}

export function resolveAgentProfileForSession(session: Session | null): AgentProfile {
  if (!session) return DEFAULT_PROFILE
  return resolveAgentProfile(session.agent_slug)
}

export function resolveAgentProfileForTask(task: UITask | null | undefined): AgentProfile {
  if (!task) return DEFAULT_PROFILE
  const profile = resolveAgentProfile(task.agentSlug)
  if (isQuickChatTask(task)) {
    return {
      ...profile,
      slots: {
        ...profile.slots,
        TrailingVisual: ChatTrailingVisual,
      },
    }
  }
  const environmentKind = task.current_environment?.kind
  if (!environmentKind) return profile

  return {
    ...profile,
    slots: {
      ...profile.slots,
      TrailingVisual: environmentKind === 'managed-sandbox' ? SandboxTrailingVisual : EnvironmentTrailingVisual,
    },
  }
}

export function resolveAgentProfileForAPITask(task: HydratedAPITask | null | undefined): AgentProfile {
  if (!task) return DEFAULT_PROFILE
  const collaborator = task.agent_collaborators?.[0]
  return resolveAgentProfile(collaborator?.slug)
}
